import React, { useState, useEffect } from 'react';
import { doc, updateDoc, collection, query, where, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../../firebase/config';
import { PaperclipIcon, TrashIcon } from '../../../components/icons/Icons';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

export default function ContractManagementModal({ vendor, appId, onClose }) {
    const [contracts, setContracts] = useState([]);
    const [purchaseHistory, setPurchaseHistory] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(true);
    
    const [contractName, setContractName] = useState('');
    const [expiryDate, setExpiryDate] = useState('');
    const [files, setFiles] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        setContracts(vendor.contracts || []);
    }, [vendor.contracts]);

    useEffect(() => {
        if (!vendor.basicInfo?.legalName) {
            setIsLoadingHistory(false);
            return;
        }

        const requestsRef = collection(db, `/artifacts/${appId}/public/data/purchaseRequests`);
        const q = query(
            requestsRef, 
            where("selectedVendor", "==", vendor.basicInfo.legalName),
            where("status", "==", "Approved")
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const history = snapshot.docs.map(doc => doc.data());
            setPurchaseHistory(history);
            setIsLoadingHistory(false);
        }, (error) => {
            console.error("Error fetching purchase history:", error);
            setIsLoadingHistory(false);
        });

        return () => unsubscribe();
    }, [vendor.basicInfo?.legalName, appId]);
    
    const handleAddContract = async (e) => {
        e.preventDefault();
        if (!contractName || !expiryDate || files.length === 0) {
            alert("Please provide a contract name, expiry date, and at least one file.");
            return;
        }
        setIsLoading(true);

        try {
            const uploadedDocs = await Promise.all(
                Array.from(files).map(async (file) => {
                    const filePath = `vendor-contracts/${appId}/${vendor.id}/${Date.now()}-${file.name}`;
                    const fileRef = ref(storage, filePath);
                    await uploadBytes(fileRef, file);
                    const url = await getDownloadURL(fileRef);
                    return { name: file.name, url, path: filePath };
                })
            );

            const newContract = {
                id: `contract_${Date.now()}`,
                contractName,
                expiryDate,
                documents: uploadedDocs
            };
            
            const updatedContracts = [...contracts, newContract];
            const vendorRef = doc(db, `/artifacts/${appId}/public/data/vendors`, vendor.id);
            await updateDoc(vendorRef, { contracts: updatedContracts });

            setContracts(updatedContracts);
            setContractName('');
            setExpiryDate('');
            setFiles([]);
            e.target.reset();

        } catch (error) {
            console.error("Error adding contract:", error);
            alert("Failed to add contract.");
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleDeleteContract = async (contractToDelete) => {
        if (!window.confirm(`Are you sure you want to delete the contract "${contractToDelete.contractName}"?`)) {
            return;
        }
        setIsLoading(true);
        try {
            if (contractToDelete.documents && contractToDelete.documents.length > 0) {
                await Promise.all(
                    contractToDelete.documents.map(doc => deleteObject(ref(storage, doc.path)))
                );
            }

            const updatedContracts = contracts.filter(c => c.id !== contractToDelete.id);
            const vendorRef = doc(db, `/artifacts/${appId}/public/data/vendors`, vendor.id);
            await updateDoc(vendorRef, { contracts: updatedContracts });
            setContracts(updatedContracts);

        } catch (error) {
            console.error("Error deleting contract:", error);
            alert("Failed to delete contract.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-4xl max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-bold text-[#213368]">Manage Contracts for: {vendor.basicInfo?.legalName}</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
                </div>

                <form onSubmit={handleAddContract} className="p-4 border rounded-lg bg-gray-50 mb-6">
                    <h3 className="text-lg font-semibold text-[#213368] mb-4">Add New Contract</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                        <div>
                             <label className="block text-sm font-medium text-gray-700 mb-1">Contract Name</label>
                            <input type="text" value={contractName} onChange={e => setContractName(e.target.value)} placeholder="e.g., Master Service Agreement" className="w-full p-2 border rounded" required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
                            <input type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)} className="w-full p-2 border rounded" required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Attachment(s)</label>
                            <label htmlFor="contract-file-upload" className="inline-block text-center w-full px-4 py-2 bg-[#20BEC6] text-white font-semibold rounded-lg shadow-sm cursor-pointer hover:bg-opacity-80 transition-colors">Choose Files</label>
                            <input id="contract-file-upload" type="file" multiple onChange={e => setFiles(e.target.files)} className="hidden" required />
                        </div>
                    </div>
                    {files.length > 0 && (
                        <div className="text-sm text-gray-500 mt-2">
                           {files.length} file(s) selected: {Array.from(files).map(f => f.name).join(', ')}
                        </div>
                    )}
                    <div className="text-right mt-4">
                        <button type="submit" disabled={isLoading} className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50">
                            {isLoading ? 'Saving...' : 'Save Contract'}
                        </button>
                    </div>
                </form>
                
                <div className="flex-grow overflow-y-auto space-y-6">
                    <div>
                        <h3 className="text-lg font-semibold text-[#213368] mb-4">Current Contracts</h3>
                        <div className="bg-white rounded-lg overflow-hidden border">
                            <table className="min-w-full divide-y divide-gray-200">
                                 <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contract Name</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expiry Date</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Documents</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {contracts.length > 0 ? contracts.map(contract => (
                                        <tr key={contract.id}>
                                            <td className="px-6 py-4 text-sm font-medium">{contract.contractName}</td>
                                            <td className="px-6 py-4 text-sm">{contract.expiryDate}</td>
                                            <td className="px-6 py-4 text-sm">
                                                {contract.documents?.map(doc => (
                                                    <a href={doc.url} key={doc.path} target="_blank" rel="noopener noreferrer" className="flex items-center text-blue-600 hover:underline mb-1">
                                                        <PaperclipIcon /> <span className="ml-1">{doc.name}</span>
                                                    </a>
                                                ))}
                                            </td>
                                            <td className="px-6 py-4 text-sm">
                                                <button onClick={() => handleDeleteContract(contract)} disabled={isLoading} className="p-1 text-red-500 hover:text-red-700 disabled:opacity-50"><TrashIcon /></button>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="4" className="text-center text-gray-500 py-8">No contracts found for this vendor.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-lg font-semibold text-[#213368] mb-4">Purchase History</h3>
                        {isLoadingHistory ? <LoadingSpinner /> : (
                            <div className="bg-white rounded-lg overflow-hidden border">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PO Number</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Approval Date</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {purchaseHistory.length > 0 ? purchaseHistory.map(pr => (
                                            <tr key={pr.requestNumber}>
                                                <td className="px-6 py-4 text-sm font-mono">{pr.requestNumber}</td>
                                                <td className="px-6 py-4 text-sm">{pr.approvalDate.toDate().toLocaleDateString()}</td>
                                                <td className="px-6 py-4 text-sm">{pr.description}</td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="3" className="text-center text-gray-500 py-8">No approved purchase history found for this vendor.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

